import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useAppDispatch } from "../../../../setup/redux/hooks";
import type { RootState } from "../../../../setup/redux/store";
import { ProductCreationWizard } from "../components/ProductCreationWizard";
import type { ProductData, ProductCreationWizardRef } from "../components/ProductCreationWizard/ProductCreationWizard";
import { ConfirmationModal } from "src/shared/components";
// Removed unused import - InventoryTracking no longer exists
import {
  fetchMerchantProducts,
  createMerchantProduct,
  updateMerchantProduct,
  fetchProductForEdit,
  clearEditProduct,
  clearEditError,
} from "../redux/merchantProductSlice";
import {
  transformApiToUIProduct,
  transformApiProductToWizardData,
} from "../utils/dataTransformers";

const AddEditMerchantProductPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { productId } = useParams<{ productId?: string }>();
  const wizardRef = useRef<ProductCreationWizardRef>(null);

  // Determine if this is edit mode
  const isEditMode = Boolean(productId);

  // State management
  const [initialProductData, setInitialProductData] = useState<
    Partial<ProductData> | undefined
  >();

  // Error modal state
  const [errorModalOpen, setErrorModalOpen] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);

  // Get state from Redux for editing
  const { 
    products: reduxProducts, 
    loading: reduxLoading,
    editProduct,
    editLoading,
    editError 
  } = useSelector((state: RootState) => state.MerchantProduct);

  // Load existing product data for editing
  useEffect(() => {
    if (isEditMode && productId) {
      // Clear any existing edit state
      dispatch(clearEditProduct());
      dispatch(clearEditError());
      
      // First, try to find product in current products list
      const existingProduct = reduxProducts.find((p) => p.id === productId);
      if (existingProduct) {
        console.log('📦 Found product in cache, using for edit:', existingProduct.id);
        try {
          const wizardData = transformApiProductToWizardData(existingProduct);
          setInitialProductData(wizardData);
        } catch (error) {
          console.error("Failed to transform cached product for editing:", error);
          setServerError("Failed to load product data for editing");
          setErrorModalOpen(true);
        }
      } else {
        // Product not in cache, fetch it specifically for editing
        console.log('🔍 Product not in cache, fetching for edit:', productId);
        dispatch(fetchProductForEdit(productId) as any);
      }
    }
    
    return () => {
      // Clean up edit state when component unmounts or productId changes
      if (isEditMode) {
        dispatch(clearEditProduct());
        dispatch(clearEditError());
      }
    };
  }, [isEditMode, productId, dispatch]);

  // Handle editProduct changes (when fetched specifically for editing)
  useEffect(() => {
    if (editProduct && isEditMode && productId === editProduct.id) {
      console.log('✅ Edit product loaded, transforming to wizard data:', editProduct.id);
      try {
        const wizardData = transformApiProductToWizardData(editProduct);
        setInitialProductData(wizardData);
      } catch (error) {
        console.error("Failed to transform fetched product for editing:", error);
        setServerError("Failed to process product data for editing");
        setErrorModalOpen(true);
      }
    }
  }, [editProduct, isEditMode, productId]);

  // Handle edit errors
  useEffect(() => {
    if (editError) {
      console.error('❌ Edit product fetch failed:', editError);
      setServerError(editError);
      setErrorModalOpen(true);
    }
  }, [editError]);

  // Handlers
  const handleProductSubmit = async (productData: ProductData) => {
    try {
      console.log("Submitting product:", productData);

      if (isEditMode && productId) {
        console.log("Updating existing product:", productId);
        const result = await dispatch(
          updateMerchantProduct({
            productId,
            productData,
          }) as any
        ).unwrap();

        if (result.success) {
          // Navigate back to product listing with success message
          navigate("/product/merchant", {
            state: {
              message: "Product updated successfully!",
              type: "success",
            },
          });
        } else {
          throw new Error("Failed to update product");
        }
      } else {
        console.log("Creating new product");
        const result = await dispatch(
          createMerchantProduct(productData) as any
        ).unwrap();

        if (result.success) {
          // Navigate back to product listing with success message
          navigate("/product/merchant", {
            state: {
              message: "Product created successfully!",
              type: "success",
            },
          });
        } else {
          throw new Error("Failed to create product");
        }
      }
    } catch (error: any) {
      console.error("Failed to submit product:", error);
      
      // Handle specific error types with user-friendly messages
      let errorMessage = "An unexpected error occurred while saving the product.";
      
      if (error.message) {
        if (error.message.includes('SKU already exists') || error.message.includes('already exists for this client')) {
          errorMessage = `SKU Conflict: The SKU "${productData.basicInfo?.productSku}" is already being used by another product. Please choose a unique SKU.`;
        } else if (error.message.includes('Authentication failed') || error.message.includes('log in again')) {
          errorMessage = 'Your session has expired. Please log in again and try submitting the product.';
        } else if (error.message.includes('Access denied') || error.message.includes('insufficient permissions')) {
          errorMessage = 'You do not have permission to perform this action. Please contact your administrator.';
        } else if (error.message.includes('Product not found') || error.message.includes('not owned by client')) {
          errorMessage = 'This product was not found or you no longer have access to it. It may have been deleted or moved.';
        } else if (error.message.includes('validation') || error.message.includes('invalid')) {
          errorMessage = `Validation Error: ${error.message}`;
        } else {
          errorMessage = error.message;
        }
      }
      
      setServerError(errorMessage);
      setErrorModalOpen(true);
    }
  };

  const handleProductCancel = () => {
    // The ProductCreationWizard component will handle unsaved changes protection
    // If there are unsaved changes, it will show a modal first
    navigate("/product/merchant");
  };

  const handleRetry = () => {
    setErrorModalOpen(false);
    // Use the ref to trigger the wizard's submit flow (which includes validation and confirmation modal)
    if (wizardRef.current) {
      wizardRef.current.openConfirmationModal();
    }
  };

  return (
    <div className="tw-flex tw-flex-1 tw-flex-col">
      {/* Product Creation Wizard - Integrated within page layout */}
      <ProductCreationWizard
        ref={wizardRef}
        onSubmit={handleProductSubmit}
        onCancel={handleProductCancel}
        initialData={initialProductData}
        loading={reduxLoading || editLoading}
        persistData={true}
        persistKey={isEditMode ? `edit-product-${productId}` : "add-product"}
        mode={isEditMode ? "edit" : "create"}
      />

      {/* Server Error Modal */}
      <ConfirmationModal
        isOpen={errorModalOpen}
        onCancel={() => setErrorModalOpen(false)}
        onConfirm={handleRetry}
        title="Submission Error"
        message={serverError || ""}
        confirmText="Try Again"
        cancelText="Close"
        confirmVariant="danger"
      />
    </div>
  );
};

export default AddEditMerchantProductPage;
